import { createError } from '@common/neo.tax';
import {
  Alert,
  colors,
  FileDisplaySection,
  FileDropZone,
  FileMimeType,
  FileWithUploadProgress,
  Icon,
  ModalActions,
  ModalHeader,
  Stack,
} from '@ui-components/neo.tax';
import { useEffect, useMemo, useState } from 'react';

import { ExcelLogo } from '@neo.tax/shared/assets';
import { ExcelParseError, ProviderType } from '@neo.tax/shared/graphql';
import { useAnalyticId } from '@neo.tax/shared/state';
import { interpretExcelParseError, validateFile } from '@neo.tax/shared/utils';

import { ScrollableUnorderedList } from '../scrollable-unordered-list';
import { Modal, StyledActionButton } from './bulk-edit-expense-project-allocation-report-modal.styled';
import { getBulkEditProviderInfo } from './bulk-edit-expense-project-allocation-report-model.utils';
import { UploadExpenseProjectAllocationReportStatus } from './hooks/upload-expense-project-allocation-report-status.enums';
import { UploadInstructions } from './UploadInstructions';

export interface BulkEditUpload {
  status: UploadExpenseProjectAllocationReportStatus;
  errors?: ExcelParseError[] | null;
  upload: (file: File) => Promise<void>;
  reset: () => void;
}

type BulkEditExpenseProjectAllocationReportModalProps = {
  providerType: ProviderType;
  isOpen: boolean;
  onClose: () => void;
  bulkEditTemplate: {
    onDownload: () => Promise<void>;
    isDownloading: boolean;
  };
  bulkEditUpload: BulkEditUpload;
};

export const BulkEditExpenseProjectAllocationReportModal = ({
  bulkEditTemplate,
  bulkEditUpload,
  isOpen,
  onClose,
  providerType,
}: BulkEditExpenseProjectAllocationReportModalProps): React.ReactElement => {
  const { analyticIds, title } = getBulkEditProviderInfo(providerType);
  const downloadAnalyticId = useAnalyticId(analyticIds.download);
  const [file, setFile] = useState<File>();
  const [fileUploads, setFileUploads] = useState<FileWithUploadProgress[]>([]);
  const { errors, reset, status, upload } = bulkEditUpload;
  const errorMessages = errors?.map((error) => interpretExcelParseError(error));

  // clear error messages if the uploaded file is changed
  useEffect(() => {
    if (!file) {
      reset();
    }
  }, [file, reset]);

  const onCancel = (): void => {
    reset();
    setFile(undefined);
    onClose();
  };

  const handleUpload = (): void => {
    if (!file) {
      throw createError('FileNotSelectedError', 'No file selected for upload');
    }
    upload(file);
  };

  useEffect(() => {
    const validateAndSetFileUploads = async (): Promise<void> => {
      if (!file) {
        setFileUploads([]);
        return;
      }

      const { errorMessage, status } = await validateFile(file);
      setFileUploads([
        {
          fileName: file.name,
          uploadPercent: null,
          status,
          errorMessage,
        },
      ]);
    };

    validateAndSetFileUploads();
  }, [file]);
  const hasFileErrors = useMemo(() => fileUploads.some((file) => file.status === 'error'), [fileUploads]);

  return (
    <Modal isOpen={isOpen} setIsOpen={onClose}>
      <Stack direction="column" gap={4}>
        <ModalHeader
          icon={<Icon node={<ExcelLogo />} size={12} variant="none" />}
          iconBgColor={colors.white}
          title={title}
        />
        {status !== UploadExpenseProjectAllocationReportStatus.SUCCESS && (
          <>
            <UploadInstructions
              providerType={providerType}
              onDownloadTemplate={bulkEditTemplate.onDownload}
              isDownloading={bulkEditTemplate.isDownloading}
            />
            <FileDropZone
              allowedMimeTypes={[FileMimeType.EXCEL]}
              addFiles={(files): void => setFile(files[0])}
              text="Your Report"
              allowMultipleSelect={false}
              inputId={downloadAnalyticId}
            />
            <FileDisplaySection
              files={fileUploads}
              removeFile={(): void => setFile(undefined)}
              disable={status === UploadExpenseProjectAllocationReportStatus.UPLOADING}
            />
          </>
        )}
        {status === UploadExpenseProjectAllocationReportStatus.SUCCESS && (
          <Alert title="Upload Success" severity="success" />
        )}
        {status === UploadExpenseProjectAllocationReportStatus.ERROR && (
          <Alert
            title="There were errors with your upload"
            subtitle={
              errorMessages ? (
                <ScrollableUnorderedList maxHeight={310}>
                  {errorMessages.map((errorMessage, index) => (
                    <li key={index}>{errorMessage}</li>
                  ))}
                </ScrollableUnorderedList>
              ) : (
                <p>
                  Unfortunately, we ran into an unknown error. Please review the uploaded file again or contact us for
                  help.
                </p>
              )
            }
            severity="error"
          />
        )}
      </Stack>
      <ModalActions hasTopBorder={false}>
        <StyledActionButton
          variant="white"
          rounded="regular"
          size="base"
          onClick={onCancel}
          disabled={status === UploadExpenseProjectAllocationReportStatus.UPLOADING}
        >
          Cancel
        </StyledActionButton>
        {status === UploadExpenseProjectAllocationReportStatus.SUCCESS ? (
          <StyledActionButton variant="primary" rounded="regular" size="base" onClick={onCancel} disabled={!file}>
            Close
          </StyledActionButton>
        ) : (
          <StyledActionButton
            variant="primary"
            rounded="regular"
            size="base"
            onClick={handleUpload}
            disabled={!file || status === UploadExpenseProjectAllocationReportStatus.UPLOADING || hasFileErrors}
          >
            {status === UploadExpenseProjectAllocationReportStatus.UPLOADING ? 'Uploading...' : 'Upload'}
          </StyledActionButton>
        )}
      </ModalActions>
    </Modal>
  );
};
